import { computed, inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Params, Router } from '@angular/router';
import { filter, map } from 'rxjs';

/** Must list every `:param` declared in the routing modules. */
type RouteParamName =
    | 'caseId'
    | 'companyId'
    | 'companyRegistrationPeriodId'
    | 'employeeHealthInsurerId'
    | 'employeeId'
    | 'hwpSurveyId'
    | 'platformLicenseId'
    | 'productId'
    | 'regulatorId';

// Merges the whole primary-outlet chain, so a consumer does not need to know which route declared a parameter.
function collectRouteParams(route: ActivatedRouteSnapshot | null): Params {
    let params: Params = {};
    let current = route;

    while (current) {
        params = { ...params, ...current.params };
        current = current.firstChild;
    }

    return params;
}

/** The signals track navigation, so a consumer that outlives a parameter change does not keep a stale snapshot. */
@Injectable({ providedIn: 'root' })
export class RouteParamsService {
    private readonly router = inject(Router);

    private readonly params = toSignal(
        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            map(() => collectRouteParams(this.router.routerState.snapshot.root))
        ),
        { initialValue: collectRouteParams(this.router.routerState.snapshot.root) }
    );

    readonly caseId = this.param('caseId');
    readonly companyId = this.param('companyId');
    readonly companyRegistrationPeriodId = this.param('companyRegistrationPeriodId');
    readonly employeeHealthInsurerId = this.param('employeeHealthInsurerId');
    readonly employeeId = this.param('employeeId');
    readonly hwpSurveyId = this.param('hwpSurveyId');
    readonly platformLicenseId = this.param('platformLicenseId');
    readonly productId = this.param('productId');
    readonly regulatorId = this.param('regulatorId');

    private param(name: RouteParamName): Signal<string | undefined> {
        return computed(() => this.params()[name]);
    }
}
